import type { VariantProps } from "class-variance-authority";
import {
  forwardRef,
  type ReactNode,
  type ButtonHTMLAttributes,
  type ElementType,
  type PropsWithChildren,
  type ComponentPropsWithoutRef,
  type ComponentPropsWithRef,
} from "react";
import { cn } from "@repo/utils";
import { buttonVariants } from "./button-variants.js";
import { Spinner } from "../ui/spinner.js";

export type BaseButtonProps = ButtonHTMLAttributes<HTMLButtonElement> &
  VariantProps<typeof buttonVariants> & {
    as?: ElementType;
    icon?: ReactNode;
    iconPosition?: "left" | "right";
    children?: ReactNode;
    loading?: boolean;
    align?: "none" | "start" | "center" | "end";
    flex?: "none" | "left" | "inline" | "right" | "up" | "down";
    justify?: "start" | "center" | "between" | "around" | "evenly";
  };

type PolymorphicButtonProps<TAs extends ElementType = "button"> = PropsWithChildren<
  BaseButtonProps & {
    as?: TAs;
  }
> &
  Omit<ComponentPropsWithoutRef<TAs>, keyof BaseButtonProps | "as">;

type PolymorphicRef<TAs extends ElementType> = ComponentPropsWithoutRef<TAs>["ref"];

type ButtonPropsWithAs<TAs extends ElementType> = BaseButtonProps &
  Omit<ComponentPropsWithRef<TAs>, keyof BaseButtonProps> & {
    as?: TAs;
  };

type ButtonComponent = <TAs extends ElementType = "button">(
  props: PolymorphicButtonProps<TAs> & { ref?: PolymorphicRef<TAs> }
) => React.ReactElement | null;

export type ButtonProps<TAs extends ElementType = "button"> = ButtonPropsWithAs<TAs>;

const ButtonInner = <TAs extends ElementType = "button">(
  {
    className,
    variant,
    size,
    fullWidth,
    icon,
    iconPosition = "left",
    flex,
    align,
    justify,
    children,
    loading,
    as,
    onClick,
    ...props
  }: PolymorphicButtonProps<TAs>,
  ref: PolymorphicRef<TAs>
) => {
  const Component = as || ("button" as ElementType);
  const isIconOnly = icon && !children;
  const finalSize = isIconOnly ? (size?.includes("icon") ? size : "icon_md") : size;
  const isDiv = Component === "div";

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (isDiv && (e.key === "Enter" || e.key === " ")) {
      e.preventDefault();
      onClick?.(e as unknown as React.MouseEvent<HTMLButtonElement>);
    }
  };

  return (
    <Component
      ref={ref}
      className={cn(
        buttonVariants({ variant, size: finalSize, align, flex, justify, fullWidth }),
        {
          group: isIconOnly,
        },
        className
      )}
      onClick={onClick}
      {...(isDiv && {
        role: "button",
        tabIndex: 0,
        onKeyDown: handleKeyDown,
      })}
      {...props}
    >
      {iconPosition === "left" && icon && (
        <span className="flex-shrink-0 transition-transform duration-150 ease-out group-hover:scale-125">
          {icon}
        </span>
      )}
      {children && (
        <>
          {loading && <Spinner className="mr-2" />}
          {children}
        </>
      )}
      {iconPosition === "right" && icon && (
        <span className="flex-shrink-0 transition-transform duration-150 ease-out group-hover:scale-125">
          {icon}
        </span>
      )}
    </Component>
  );
};

export const Button = forwardRef(ButtonInner) as ButtonComponent;

ButtonInner.displayName = "Button";
